import React, { useEffect } from 'react';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { Header } from '../components/Header';
import { Button } from '../components/Button';
import { formatCurrency, getDaysUntil } from '../utils/format';
import { useAppStore } from '../store/useAppStore';
import { apiClient } from '../api/client';
import { useTranslation } from 'react-i18next';
import { PremiumFeature } from '../components/PremiumFeature';
import { AddGoalDepositFlow, GoalDepositData, FREQUENCY_LABELS } from '../components/AddGoalDepositFlow';
import { GoalModal } from '../components/GoalModal';
import { DepositModal } from '../components/DepositModal';
import { RecurringGoalDeposit } from '../types';
import { useWebPowerSyncGoals } from '../hooks/usePowerSync';
import './GoalsScreen.css';

export const GoalsScreen: React.FC = () => {
  const { goals: storeGoals, setGoals, setLoading, settings, currentPage } = useAppStore();
  const { t } = useTranslation();
  
  // PowerSync данные
  const { goals: psGoals } = useWebPowerSyncGoals();
  const goals = psGoals.length > 0 ? psGoals : storeGoals;
  const [isPremiumRequired, setIsPremiumRequired] = React.useState(false);
  const [showAddGoalModal, setShowAddGoalModal] = React.useState(false);
  const [showDepositModal, setShowDepositModal] = React.useState(false);
  const [showEditGoalModal, setShowEditGoalModal] = React.useState(false);
  const [showEditDepositModal, setShowEditDepositModal] = React.useState(false);
  const [selectedGoalId, setSelectedGoalId] = React.useState<string | null>(null);
  const [selectedDepositId, setSelectedDepositId] = React.useState<string | null>(null);
  const [recurringDeposits, setRecurringDeposits] = React.useState<RecurringGoalDeposit[]>([]);
  const [showRecurringDeposits, setShowRecurringDeposits] = React.useState(false);
  
  /**
   * ✅ Форматирует дату из строки, которая уже в локальном timezone пользователя
   * Дата приходит в формате "YYYY-MM-DDTHH:MM:SS" (без Z, уже сконвертирована на бэкенде)
   * JavaScript интерпретирует такую строку как локальное время, что нам и нужно
   */
  const formatLocalDate = (dateString: string): string => {
    if (!dateString) return '';
    
    try {
      // Парсим строку как локальное время (без timezone)
      const date = new Date(dateString);
      
      // Проверяем валидность даты
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', dateString);
        return '';
      }
      
      // Форматируем дату в нужный формат
      return date.toLocaleDateString('ru-RU', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Синхронизируем PowerSync цели в store
  useEffect(() => {
    if (psGoals.length > 0) {
      setGoals(psGoals as any);
    }
  }, [psGoals]);

  useEffect(() => {
    loadGoalsAndLimits();
    loadRecurringDeposits();
    
    // Слушаем событие открытия модального окна
    const handleOpenModal = () => setShowAddGoalModal(true);
    window.addEventListener('openAddGoalModal', handleOpenModal);
    
    // Обновляем данные при возврате на экран (visibility change)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadGoalsAndLimits();
        loadRecurringDeposits();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Периодическое обновление автопополнений каждые 30 секунд
    const intervalId = setInterval(() => {
      loadRecurringDeposits();
    }, 30000);
    
    return () => {
      window.removeEventListener('openAddGoalModal', handleOpenModal);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, []);

  // Обновляем данные при каждом переходе на вкладку Целей
  useEffect(() => {
    if (currentPage === 'goals') {
      loadRecurringDeposits();
    }
  }, [currentPage]);

  const loadGoalsAndLimits = async () => {
    // Если PowerSync уже предоставил данные целей, пропускаем API
    if (psGoals.length > 0) {
      console.log('🎯 [PowerSync] Using PowerSync goals, skipping API call');
      setGoals(psGoals as any);
      return;
    }
    try {
      setLoading(true);
      setIsPremiumRequired(false);
      
      // Загружаем цели
      const data = await apiClient.getLimitsAndGoalsData();
      console.log('📊 Loaded goals data:', data);
      setGoals(data.goals || []);
    } catch (error: any) {
      console.error('Error loading goals and limits:', error);
      if (error?.message?.includes('premium') || error?.message?.includes('subscription')) {
        setIsPremiumRequired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecurringDeposits = async () => {
    try {
      const data = await apiClient.getRecurringGoalDeposits();
      setRecurringDeposits(data.deposits || []);
    } catch (error: any) {
      console.error('Error loading recurring deposits:', error);
    }
  };

  const handleGoalSubmit = async (goal: Partial<{ id: string; name: string; icon?: string; target_amount?: number | null }>) => {
    try {
      setLoading(true);
      if (goal.id) {
        await apiClient.updateGoal(goal.id, {
          name: goal.name!,
          icon: goal.icon,
          target_amount: goal.target_amount ?? undefined
        });
        setShowEditGoalModal(false);
        setSelectedGoalId(null);
      } else {
        await apiClient.addGoal({
          name: goal.name!,
          icon: goal.icon,
          target_amount: goal.target_amount ?? undefined
        });
      }
      
      await loadGoalsAndLimits();
      setShowAddGoalModal(false);
    } catch (error: any) {
      console.error('Error adding goal:', error);
      alert(error?.message || t('goals.createFailedRetry'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleGoalDeposit = async (depositData: GoalDepositData) => {
    if (!selectedGoalId) return;
    
    try {
      setLoading(true);
      
      if (depositData.type === 'onetime') {
        // Разовое пополнение
        await apiClient.depositToGoal(selectedGoalId, depositData.amount);
        
        // Перезагружаем список целей
        await loadGoalsAndLimits();
      } else {
        // Регулярное пополнение
        await apiClient.createRecurringGoalDeposit({
          goal_id: parseInt(selectedGoalId),
          title: `Пополнение цели`,
          amount: depositData.amount,
          frequency: depositData.frequency!,
          day_of_week: depositData.dayOfWeek,
          day_of_month: depositData.dayOfMonth,
          start_date: depositData.startDate,
          notification_time: depositData.notificationTime || '09:00'
        });
        
        // Перезагружаем список автопополнений
        await loadRecurringDeposits();
        
        // Показываем блок с автопополнениями если он был скрыт
        setShowRecurringDeposits(true);
      }
      
      // Закрываем модальное окно
      setShowDepositModal(false);
      setSelectedGoalId(null);
      
      alert(depositData.type === 'onetime' ? t('goals.depositOneTimeSuccess') : t('goals.recurringDepositConfigured'));
    } catch (error: any) {
      console.error('Error depositing to goal:', error);
      alert(error?.message || t('goals.depositFailedRetry'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleCloseGoal = async (goalId: string) => {
    const goal = goals.find(g => g.id === goalId);
    if (!goal) return;

    const confirmMessage = goal.current_amount > 0
      ? t('goals.closeGoalConfirmWithRefund', {
          name: goal.name,
          amount: formatCurrency(goal.current_amount, settings?.currency || 'RUB')
        })
      : t('goals.closeGoalConfirm', { name: goal.name });

    if (!confirm(confirmMessage)) return;

    try {
      setLoading(true);
      await apiClient.closeGoal(goalId);
      await loadGoalsAndLimits();
      alert(t('goals.closeSuccess'));
    } catch (error: any) {
      console.error('Error closing goal:', error);
      alert(error?.message || t('goals.closeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (goalId: string, goalName: string) => {
    if (!confirm(t('goals.deleteGoalConfirm', { name: goalName }))) return;

    try {
      setLoading(true);
      await apiClient.deleteGoal(goalId);
      await loadGoalsAndLimits();
      alert(t('goals.deleteSuccess'));
    } catch (error: any) {
      console.error('Error deleting goal:', error);
      alert(error?.message || t('goals.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreGoal = async (goalId: string, goalName: string) => {
    if (!confirm(t('goals.restoreGoalConfirm', { name: goalName }))) return;

    try {
      setLoading(true);
      await apiClient.restoreGoal(goalId);
      await loadGoalsAndLimits();
      alert(t('goals.restoreSuccess'));
    } catch (error: any) {
      console.error('Error restoring goal:', error);
      alert(error?.message || t('goals.restoreFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePauseGoal = async (goalId: string, goalName: string, isPaused: boolean) => {
    const confirmKey = isPaused ? 'goals.resumeGoalConfirm' : 'goals.pauseGoalConfirm';
    if (!confirm(t(confirmKey, { name: goalName }))) return;

    try {
      setLoading(true);
      await apiClient.togglePauseGoal(goalId);
      await loadGoalsAndLimits();
      alert(isPaused ? t('goals.resumeSuccess') : t('goals.pauseSuccess'));
    } catch (error: any) {
      console.error('Error toggling pause goal:', error);
      alert(error?.message || t('goals.togglePauseFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecurringDeposit = async (depositId: string, depositTitle: string) => {
    if (!confirm(t('goals.deleteAutoDepositConfirm', { title: depositTitle }))) return;

    try {
      setLoading(true);
      await apiClient.deleteRecurringGoalDeposit(depositId);
      await loadRecurringDeposits();
      alert(t('goals.autoDepositDeleted'));
    } catch (error: any) {
      console.error('Error deleting recurring deposit:', error);
      alert(error?.message || t('goals.autoDepositDeleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRecurringDeposit = async (depositId: string, isActive: boolean) => {
    try {
      setLoading(true);
      await apiClient.toggleRecurringGoalDeposit(depositId);
      await loadRecurringDeposits();
      alert(isActive ? t('goals.autoDepositDeactivated') : t('goals.autoDepositActivated'));
    } catch (error: any) {
      console.error('Error toggling recurring deposit:', error);
      alert(error?.message || t('goals.autoDepositStatusFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEditRecurringDeposit = (depositId: string) => {
    setSelectedDepositId(depositId);
    setShowEditDepositModal(true);
  };

  const handleUpdateDeposit = async (depositData: GoalDepositData) => {
    if (!selectedDepositId) return;
    
    try {
      setLoading(true);
      
      await apiClient.updateRecurringGoalDeposit(selectedDepositId, {
        title: `Пополнение цели`,
        amount: depositData.amount,
        frequency: depositData.frequency!,
        day_of_week: depositData.dayOfWeek,
        day_of_month: depositData.dayOfMonth,
        start_date: depositData.startDate,
        notification_time: depositData.notificationTime || '09:00'
      });
      
      // Перезагружаем список автопополнений
      await loadRecurringDeposits();
      
      // Закрываем модальное окно
      setShowEditDepositModal(false);
      setSelectedDepositId(null);
      
      alert(t('goals.autoDepositUpdated'));
    } catch (error: any) {
      console.error('Error updating recurring deposit:', error);
      alert(error?.message || t('goals.autoDepositUpdateFailed'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getProgress = (current: number, target?: number) => {
    if (!target) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const getGoalStatus = (progress: number, deadline?: string) => {
    if (progress >= 100) {
      return { status: 'completed', text: 'Достигнуто!', color: '#34C759' };
    }
    
    if (deadline) {
      const daysLeft = getDaysUntil(deadline);
      if (daysLeft < 0) {
        return { status: 'overdue', text: 'Просрочено', color: '#FF3B30' };
      }
      if (progress > 75) {
        return { status: 'almost', text: 'Почти готово', color: '#FF9500' };
      }
    }
    
    return { status: 'in_progress', text: 'В процессе', color: '#007AFF' };
  };

  return (
    <PremiumFeature featureId="goals">
      <div className="goals-screen page">
      <Header title={t('goals.title')} />
      
      {/* Summary */}
      <div className="section">
        <div className="goals-summary">
          <Card padding="medium" className="neo-static">
            <div className="goals-summary__item">
              <div className="goals-summary__icon">
                <Icon name="target" size={32} />
              </div>
              <div className="goals-summary__content">
                <div className="goals-summary__label">{t('goals.activeGoals')}</div>
                <div className="goals-summary__value">{goals.filter(g => g.status !== 'CLOSED').length}</div>
              </div>
            </div>
          </Card>

          <Card padding="medium" className="neo-static">
            <div className="goals-summary__item">
              <div className="goals-summary__icon">
                <Icon name="wallet" size={32} />
              </div>
              <div className="goals-summary__content">
                <div className="goals-summary__label">{t('goals.saved')}</div>
                <div className="goals-summary__value">
                  {formatCurrency(goals.filter(g => g.status !== 'CLOSED').reduce((sum, g) => sum + g.current_amount, 0), settings?.currency || 'RUB')}
                </div>
              </div>
            </div>
          </Card>

          <Card padding="medium" className="neo-static">
            <div className="goals-summary__item">
              <div className="goals-summary__icon">
                <Icon name="flag" size={32} />
              </div>
              <div className="goals-summary__content">
                <div className="goals-summary__label">{t('goals.target')}</div>
                <div className="goals-summary__value">
                  {formatCurrency(goals.filter(g => g.status !== 'CLOSED').reduce((sum, g) => sum + (g.target_amount || 0), 0), settings?.currency || 'RUB')}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Add Goal Button moved under summary */}
      <div className="section">
        <button
          className="goals-add-button neo-raised"
          onClick={() => setShowAddGoalModal(true)}
        >
          Добавить цель
        </button>
      </div>

      {/* Active Goals List */}
      <div className="section">
        <h3 className="section__title">{t('goals.yourGoals')}</h3>
        {isPremiumRequired ? (
          <Card padding="large" className="neo-static">
            <div className="empty-state">
              <div className="empty-state__icon">
                <Icon name="star" size={48} color="#FF9500" />
              </div>
              <div className="empty-state__title">Требуется Premium</div>
              <div className="empty-state__subtitle">
                Финансовые цели доступны только для Premium пользователей
              </div>
            </div>
          </Card>
        ) : (() => {
          const activeGoals = goals.filter(g => g.status !== 'CLOSED'); // Включает ACTIVE, ACHIEVED, PAUSED
          const closedGoals = goals.filter(g => g.status === 'CLOSED');
          
          return (
            <>
              {activeGoals.length > 0 ? (
                <div className="goals-list">
                  {activeGoals.map((goal) => {
                    const progress = getProgress(goal.current_amount, goal.target_amount);
                    const status = getGoalStatus(progress, goal.deadline);
                    const daysLeft = goal.deadline ? getDaysUntil(goal.deadline) : null;
                    const isAchieved = goal.status === 'ACHIEVED';

                    return (
                      <Card key={goal.id} padding="medium" className="goal-card neo-static">
                        <div className="goal-card__header">
                          <div className="goal-card__info">
                            <div className="goal-card__name">
                              {goal.icon && (
                                <Icon name={goal.icon} size={20} style={{ marginRight: '8px' }} />
                              )}
                              {goal.name}
                            </div>
                            {goal.target_amount && (
                              <div className="goal-card__target">
                                {formatCurrency(goal.current_amount, settings?.currency || 'RUB')} / {formatCurrency(goal.target_amount, settings?.currency || 'RUB')} ({progress.toFixed(0)}%)
                              </div>
                            )}
                            {!goal.target_amount && (
                              <div className="goal-card__target">
                                Накоплено: {formatCurrency(goal.current_amount, settings?.currency || 'RUB')}
                              </div>
                            )}
                          </div>
                          <button 
                            className="goal-card__close-btn neo-raised"
                            onClick={() => handleCloseGoal(goal.id)}
                            title="Закрыть цель"
                          >
                            <Icon name="close" size={16} />
                          </button>
                        </div>

                        {goal.target_amount && (
                          <>
                            <div className="goal-card__progress">
                              <div className="goal-card__progress-bar">
                                <div 
                                  className="goal-card__progress-fill"
                                  style={{ 
                                    width: `${progress}%`,
                                    backgroundColor: isAchieved ? '#34C759' : status.color
                                  }}
                                />
                              </div>
                            </div>

                            {daysLeft !== null && (
                              <div className="goal-card__deadline">
                                <Icon name="calendar" size={14} color="#86868B" />
                                <span>
                                  {daysLeft > 0 ? `Осталось ${daysLeft} дней` : 
                                   daysLeft === 0 ? 'Сегодня' : 
                                   `Просрочено на ${Math.abs(daysLeft)} дней`}
                                </span>
                              </div>
                            )}
                          </>
                        )}

                        <div className="goal-card__footer">
                          <button 
                            className="goal-card__action neo-raised"
                            onClick={() => {
                              setSelectedGoalId(goal.id);
                              setShowDepositModal(true);
                            }}
                          >
                            <Icon name="add" size={14} />
                            <span>Пополнить</span>
                          </button>
                          <button 
                            className="goal-card__action neo-raised"
                            onClick={() => {
                              setSelectedGoalId(goal.id);
                              setShowEditGoalModal(true);
                            }}
                          >
                            <Icon name="edit" size={14} />
                            <span>Редактировать</span>
                          </button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card padding="large" className="neo-static">
                  <div className="empty-state">
                    <div className="empty-state__icon">
                      <Icon name="target" size={48} color="#8E8E93" />
                    </div>
                    <div className="empty-state__title">Нет целей</div>
                    <div className="empty-state__subtitle">
                      Создайте первую финансовую цель
                    </div>
                  </div>
                </Card>
              )}

              {/* Closed Goals Section */}
              {closedGoals.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h3 className="section__title" style={{ marginBottom: '12px', fontSize: '14px', color: '#8E8E93', fontWeight: '500' }}>Закрытые цели</h3>
                  <div className="goals-list">
                    {closedGoals.map((goal) => {
                      // ✅ Используем formatLocalDate для правильного парсинга даты в локальном timezone
                      const closedDate = goal.closed_at ? formatLocalDate(goal.closed_at) : '';
                      return (
                        <div key={goal.id} style={{ opacity: 0.65 }}>
                          <Card padding="medium" className="goal-card goal-card--closed neo-static">
                            <div className="goal-card__header">
                              <div className="goal-card__info">
                                <div className="goal-card__name" style={{ color: '#8E8E93' }}>
                                  {goal.icon && (
                                    <Icon name={goal.icon} size={20} style={{ marginRight: '8px' }} />
                                  )}
                                  {goal.name}
                                </div>
                                {goal.target_amount && (
                                  <div className="goal-card__target" style={{ color: '#8E8E93' }}>
                                    Целевая сумма: {formatCurrency(goal.target_amount, settings?.currency || 'RUB')}
                                  </div>
                                )}
                                {!goal.target_amount && (
                                  <div className="goal-card__target" style={{ color: '#8E8E93' }}>
                                    Копилка (без цели)
                                  </div>
                                )}
                                {closedDate && (
                                  <div className="goal-card__target" style={{ color: '#8E8E93', fontSize: '12px', marginTop: '4px' }}>
                                    Закрыта: {closedDate}
                                  </div>
                                )}
                              </div>
                              <div 
                                className="goal-card__status"
                                style={{ 
                                  backgroundColor: 'transparent',
                                  color: 'var(--color-text-primary)',
                                  border: '2px solid var(--color-divider)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                <Icon name="lock" size={14} color="var(--color-text-primary)" />
                                <span>Закрыта</span>
                              </div>
                            </div>
                            
                            <div className="goal-card__footer" style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                              <button 
                                className="goal-card__action goal-card__action--danger neo-raised"
                                onClick={() => handleDeleteGoal(goal.id, goal.name)}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: '#FF3B30',
                                  border: '2px solid #FF3B3040'
                                }}
                              >
                                <Icon name="trash" size={14} />
                                <span>Удалить</span>
                              </button>
                              <button 
                                className="goal-card__action neo-raised"
                                onClick={() => handleRestoreGoal(goal.id, goal.name)}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: '#34C759',
                                  border: '2px solid #34C75940'
                                }}
                              >
                                <Icon name="refresh" size={14} />
                                <span>Восстановить</span>
                              </button>
                            </div>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}


              {/* Recurring Deposits Section */}
              {recurringDeposits.length > 0 && (
                <div style={{ marginTop: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <h3 className="section__title" style={{ fontSize: '14px', color: '#8E8E93', fontWeight: '500', margin: 0 }}>
                      Автопополнения целей
                    </h3>
                    <button 
                      onClick={() => setShowRecurringDeposits(!showRecurringDeposits)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#007AFF',
                        fontSize: '14px',
                        cursor: 'pointer',
                        padding: '4px 8px'
                      }}
                    >
                      {showRecurringDeposits ? 'Скрыть' : 'Показать'}
                    </button>
                  </div>

                  {showRecurringDeposits && (
                    <div className="goals-list">
                      {recurringDeposits.map((deposit) => {
                        const frequencyText = FREQUENCY_LABELS[deposit.frequency] || deposit.frequency;

                        // Парсим дату правильно с учетом того, что это DATE (без времени)
                        const dateStr = deposit.next_deposit_date;
                        const [year, month, day] = dateStr.split(/[-T]/);
                        const nextDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        });

                        return (
                          <div key={deposit.id} style={{ opacity: deposit.is_active ? 1 : 0.6 }}>
                            <Card padding="medium" className="goal-card neo-static">
                              <div className="goal-card__header">
                              <div className="goal-card__info">
                                <div className="goal-card__name">{deposit.goal_name}</div>
                                <div className="goal-card__target">
                                  {formatCurrency(deposit.amount, settings?.currency || 'RUB')} • {frequencyText}
                                </div>
                                <div className="goal-card__target" style={{ fontSize: '12px', marginTop: '4px', color: '#8E8E93' }}>
                                  ⏰ {deposit.notification_time} • Следующее: {nextDate}
                                </div>
                              </div>
                              <div 
                                className="goal-card__status"
                                style={{ 
                                  backgroundColor: 'transparent',
                                  color: deposit.is_active ? '#34C759' : '#8E8E93',
                                  border: `2px solid ${(deposit.is_active ? '#34C759' : '#8E8E93')}40`
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  {deposit.is_active ? (
                                    <>
                                      <Icon name="check" size={14} color="#34C759" />
                                      <span>Активно</span>
                                    </>
                                  ) : (
                                    <>
                                      <Icon name="pause" size={14} color="#8E8E93" />
                                      <span>Остановлено</span>
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>

                              <div className="goal-card__footer" style={{ marginTop: '12px' }}>
                                <button 
                                  className="goal-card__action neo-raised"
                                  onClick={() => handleToggleRecurringDeposit(deposit.id, deposit.is_active)}
                                  style={{
                                    color: deposit.is_active ? '#FF9500' : '#34C759'
                                  }}
                                >
                                  <Icon name={deposit.is_active ? "pause" : "play_arrow"} size={14} />
                                  <span>{deposit.is_active ? 'Остановить' : 'Возобновить'}</span>
                                </button>
                                <button 
                                  className="goal-card__action neo-raised"
                                  onClick={() => handleEditRecurringDeposit(deposit.id)}
                                  style={{
                                    color: '#007AFF'
                                  }}
                                >
                                  <Icon name="edit" size={14} />
                                  <span>Изменить</span>
                                </button>
                                <button 
                                  className="goal-card__action goal-card__action--danger neo-raised"
                                  onClick={() => handleDeleteRecurringDeposit(deposit.id, deposit.title)}
                                  style={{
                                    color: '#FF3B30'
                                  }}
                                >
                                  <Icon name="delete" size={14} />
                                  <span>Удалить</span>
                                </button>
                              </div>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Add Goal Modal */}
      <GoalModal
        visible={showAddGoalModal && !isPremiumRequired}
        onClose={() => setShowAddGoalModal(false)}
        onSubmit={handleGoalSubmit}
        goal={null}
        currency={settings?.currency}
      />

      {/* Premium Required Modal */}
      {showAddGoalModal && isPremiumRequired && (
        <div 
          className="modal-overlay modal-overlay--center"
          onClick={() => setShowAddGoalModal(false)}
        >
          <div
            className="modal-content"
            style={{ 
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px', 
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '20px', textAlign: 'center' }}>
              Требуется Premium
            </h3>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ marginBottom: '16px' }}>
                <Icon name="star" size={48} color="#1C78FF" />
              </div>
              <p style={{ color: '#86868B', marginBottom: '20px', fontSize: '16px' }}>
                Создание целей доступно только для Premium пользователей
              </p>
              <p style={{ color: '#86868B', fontSize: '14px', marginBottom: '24px' }}>
                Оформите Premium подписку через Telegram бот
              </p>
              <Button 
                variant="primary" 
                onClick={() => setShowAddGoalModal(false)}
                style={{ width: '100%' }}
              >
                Понятно
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Deposit to Goal Flow */}
      {showDepositModal && selectedGoalId && (
        <AddGoalDepositFlow
          goalId={selectedGoalId}
          goalName={goals.find(g => g.id === selectedGoalId)?.name || 'Цель'}
          onClose={() => {
            setShowDepositModal(false);
            setSelectedGoalId(null);
          }}
          onSubmit={handleGoalDeposit}
          currency={settings?.currency}
          currencyCode={settings?.currency_code}
        />
      )}

      {/* Edit Goal Modal */}
      <GoalModal
        visible={!!(showEditGoalModal && selectedGoalId)}
        onClose={() => {
          setShowEditGoalModal(false);
          setSelectedGoalId(null);
        }}
        onSubmit={handleGoalSubmit}
        goal={goals.find(g => g.id === selectedGoalId) ?? undefined}
        currency={settings?.currency}
      />

      {/* Edit Recurring Deposit Flow */}
      {showEditDepositModal && selectedDepositId && (() => {
        const deposit = recurringDeposits.find(d => d.id === selectedDepositId);
        if (!deposit) return null;
        
        // Парсим дату из next_deposit_date
        const dateStr = deposit.next_deposit_date || deposit.start_date;
        const [year, month, day] = dateStr.split(/[-T\s]/);
        const startDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        
        return (
          <AddGoalDepositFlow
            goalId={deposit.goal_id}
            goalName={deposit.goal_name}
            initialData={{
              type: 'recurring',
              amount: deposit.amount,
              frequency: deposit.frequency,
              dayOfWeek: deposit.day_of_week ?? undefined,
              dayOfMonth: deposit.day_of_month ?? undefined,
              startDate: startDate,
              notificationTime: deposit.notification_time
            }}
            onClose={() => {
              setShowEditDepositModal(false);
              setSelectedDepositId(null);
            }}
            onSubmit={handleUpdateDeposit}
            currency={settings?.currency}
            currencyCode={settings?.currency_code}
            isEditMode={true}
          />
        );
      })()}

      </div>
    </PremiumFeature>
  );
};
